import React, { useEffect, useRef } from "react";
import Styles from "./selectMonthSection.module.scss";
import useOnScreen from "@src/utils/hooks/useOnScreen";
import { useLocation } from "react-router-dom";

export const generateDates = () => {
  const dates: { monthsInYear: string[] }[] = [];
  const currentDate = new Date(); // 현재 날짜
  let year = currentDate.getFullYear();
  let month = currentDate.getMonth() + 1; // 월은 0부터 시작하므로 +1

  let monthsInYear = []; // 같은 해의 월 데이터를 저장할 배열

  // 24개월 동안 반복
  for (let i = 0; i < 24; i++) {
    // 'YYYY-MM' 형식으로 월을 저장
    const formattedMonth = `${year}-${String(month).padStart(2, "0")}`;
    monthsInYear.push(formattedMonth);

    // 월이 12월이면 다음 해로 넘어가므로, 객체로 저장 후 새로운 배열을 생성
    if (month === 12) {
      dates.push({ monthsInYear: monthsInYear });
      monthsInYear = [];
      year++; // 년도를 다음 해로
      month = 1; // 월은 1월로
    } else {
      month++;
    }
  }

  // 마지막 해의 데이터를 push
  if (monthsInYear.length > 0) {
    dates.push({ monthsInYear });
  }

  return dates;
};

export const MONTHS_IN_YEAR = generateDates();
const EL_DAY_TAB_HEIGHT = 64; // 월, 상세 선택 탭 높이

type Props = {
  handleComplete: () => void;
  handleReset: () => void;
  selectedMonths: string[];
  setSelectedMonths: (value: React.SetStateAction<string[]>) => void;
  selectedDate: { startDate: string; endDate: string };
};

export default function SelectMonthSection({
  handleComplete,
  handleReset,
  selectedMonths,
  setSelectedMonths,
  selectedDate,
}: Props) {
  const location = useLocation();
  const isProductTab = location.search.includes('tabId=2');

  const sectionWrapRef = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(sectionWrapRef);

  const handleSelectDate = (yearIndex: number, monthIndex: number) => {
    if (selectedMonths.includes(MONTHS_IN_YEAR[yearIndex].monthsInYear[monthIndex])) {
      // 이미 선택된 날짜라면 선택 해제
      setSelectedMonths(
        selectedMonths.filter(
          (date) => date !== MONTHS_IN_YEAR[yearIndex].monthsInYear[monthIndex],
        ),
      );
      return;
    }
    setSelectedMonths([...selectedMonths, MONTHS_IN_YEAR[yearIndex].monthsInYear[monthIndex]]);
  };

  // 전체선택 버튼 클릭
  const handleSelectAllDate = (yearIndex: number) => {
    // 현재 선택된 월 중 해당 연도의 월들이 모두 선택되었는지 확인
    const isAllSelected = MONTHS_IN_YEAR[yearIndex].monthsInYear.every((month) => selectedMonths.includes(month));
    if(isAllSelected){
      setSelectedMonths((prev) =>prev.filter((month) => !MONTHS_IN_YEAR[yearIndex].monthsInYear.includes(month)));
    }else{
      // 해당 년도가 모두 선택되어있지 않으면 모두 선택하며 기존에 선택된 날짜는 중복되지 않게 필터링
      const months = MONTHS_IN_YEAR[yearIndex].monthsInYear.filter((month) => !selectedMonths.includes(month),);

      setSelectedMonths([...selectedMonths, ...months]);
    }
  };

  const modifiedDate = (date: string) => {
    const month = date.slice(5, 7);
    // 앞에 있는 0 제거 (10월 미만의 월만 해당)
    return month.startsWith("0") ? month.slice(1) : month;
  };

  useEffect(() => {
    // 해당 컴포넌트가 화면에 보일때 날짜가 선택되어있다면 선택된 날짜로 스크롤 시킴
    if (isVisible) {
      if (sectionWrapRef.current) {
        const sectionWrapElement: HTMLElement = sectionWrapRef.current;
        const titleElement: HTMLElement = sectionWrapRef.current.querySelector(
          `.${Styles["selected-btn"]}`,
        );
        if (titleElement) {
          sectionWrapRef.current.scrollTop =
            titleElement.offsetTop - sectionWrapElement.offsetTop - EL_DAY_TAB_HEIGHT;
        }
      }
    }
  }, [isVisible]);

  return (
    <>
      <section
        ref={sectionWrapRef}
        className={Styles["section-container"]}
        style={{ padding: "0 20px" }}
      >
        <div className={Styles["section-wrap"]}>
          {MONTHS_IN_YEAR.map((date, yearIndex) => (
            <div key={yearIndex} className={Styles["year-wrap"]}>
              <div className={Styles["title-wrap"]}>
                <p>{MONTHS_IN_YEAR[yearIndex].monthsInYear[0].slice(0, 4)}</p>
                <button onClick={() => handleSelectAllDate(yearIndex)}>
                  {MONTHS_IN_YEAR[yearIndex].monthsInYear.every((month) => selectedMonths.includes(month)) ? "전체취소" : "전체선택"}
                </button>
              </div>
              <ul className={Styles["months-wrap"]}>
                {date.monthsInYear.map((monthInYear, monthIndex) => (
                  <li key={monthIndex}>
                    <button
                      onClick={() => handleSelectDate(yearIndex, monthIndex)}
                      className={
                        selectedMonths.includes(monthInYear)
                          ? Styles["selected-btn"]
                          : Styles["un-selected-btn"]
                      }
                    >
                      {modifiedDate(monthInYear)}월
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className={Styles["fake-box"]}></div>
      </section>
      <div className={Styles["btn-wrap"]}>
        <button
          className={Styles["reset-btn"]}
          onClick={handleReset}
          disabled={selectedMonths.length === 0 && selectedDate.startDate === ""}
        >
          초기화
        </button>
        <button className={Styles["complete-btn"]} onClick={() => handleComplete()}>
          {isProductTab && '적용'}
          {!isProductTab && (selectedMonths.length === 0 && selectedDate.startDate === "" ? '건너뛰기': '다음')}
        </button>
      </div>
    </>
  );
}
